import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { billingScopes } from '../../billing-consts';
import { getFullPath } from '../../lib/get-full-path';
import { disableBodyScroll } from '../../lib/toggle-body-scroll';
import { AssetService } from '../../services/asset.service';
import { GalleryService } from '../../services/gallery.service';
import { PermissionService } from '../../services/permission.service';
import { RedirectService } from '../../services/redirect.service';

@Injectable({
  providedIn: 'root',
})
export class AssetVersionGuard {
  private assetService = inject(AssetService);
  private platformId = inject<string>(PLATFORM_ID);
  private redirectService = inject(RedirectService);
  private galleryService = inject(GalleryService);
  private permissionService = inject(PermissionService);

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    if (isPlatformServer(this.platformId)) return true;
    const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next);
    const galleryPermissions = await firstValueFrom(this.galleryService.getGalleryPermissions(gallery));

    const allowHistory =
      (gallery.version_history_enabled || galleryPermissions.can_edit) &&
      this.permissionService.canDisplayElement(billingScopes.assetVersion.multiple);

    if (allowHistory) {
      const assetId = next.params.assetId.replace('}', '');
      const versions = await firstValueFrom(this.assetService.getAssetVersions(assetId, gallery.id));

      const version = versions.find(v => v.id === next.params.versionId);
      if (version) {
        disableBodyScroll('asset-page');
        return true;
      }
    }

    const route = getFullPath(next);

    return this.redirectService.redirectTo(
      route.slice(
        0,
        route.findIndex(p => p === 'version')
      )
    );
  }
}
